import { useCallback, useMemo, useState } from "react";
import sorting from "../Utils/sorting";
import sortingAndFiltering from "../Utils/sortingAndFiltering";

/**
 * Custom React hook for sorting and filtering a list based on search text, keys, and custom comparers.
 *
 * @param {Object} params - The parameters object.
 * @param {Array} params.list - The list of items to be sorted and filtered.
 * @param {string} params.searchText - The text to filter the list by.
 * @param {Array<string>} params.searchableKeys - Keys in the list items to search through, for example column names in a table.
 * @param {Array<string>} [params.customFilterKeys] - Optional custom filter keys.
 * @param {Function} params.defaultComparer - Optional default comparer function for sorting used if no sort key is selected.
 * @returns {Object} An object containing the filtered and sorted list, current sort key, sort order, and a handler to change sorting.
 *
 * @example
 * const { filteredAndSortedList, sortKey, sortOrder, handleSortChange } = useSortingAndFiltering({
 *   list: data,
 *   searchText: "apple",
 *   searchableKeys: ["name", "category"],
 *   customFilterKeys: ["status"],
 *   defaultComparer: (a, b) => a.id - b.id
 * });
 */
function useSortingAndFiltering({ list, searchText, searchableKeys, customFilterKeys, defaultComparer }) {
    const [sortKey, setSortKey] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const handleSortChange = useCallback((key) => {
        if (key !== sortKey) {
            setSortKey(key);
            setSortOrder('asc');
        } else {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        }
    }, [sortKey, sortOrder]);

    const comparer = sortKey ? sorting.compareValues(sortKey, sortOrder) : defaultComparer;

    const filteredAndSortedList = useMemo(() => {
        return sortingAndFiltering.filterAndSort({
            list,
            searchText,
            searchableKeys,
            customFilterKeys,
            comparer
        });
    }, [list, searchText, sortKey, sortOrder, searchableKeys, customFilterKeys, defaultComparer, comparer]);
    
    const getSortIcon = useCallback((column) => {
        if (sortKey !== column) return " ↕";
        return sortOrder === "asc" ? " ↑" : " ↓";
    }, [sortKey, sortOrder]);
    
    return { filteredAndSortedList, sortKey, sortOrder, handleSortChange, getSortIcon };
}

export default useSortingAndFiltering;
